import { ArrowRight, Pencil, Plus, Send, Trash2, Undo2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PodgladHurtu } from '@/components/PodgladHurtu';
import { PodgladZmian } from '@/components/PodgladZmian';
import { Button } from '@/components/ui/button';
import {
  MAX_ZMIAN_HURTEM,
  MAX_ZMIAN_W_OPERACJI,
  validateCell,
  type AuthoringTable,
  type DraftChange,
} from '@/lib/authoring-api';
import type { WezelBazy, WynikZapytania } from '@/lib/baza-api';
import {
  indeksKolumny,
  kolumnaEdytowalna,
  liczbaOperacji,
  pustaEdycja,
  ustawWszedzie,
  usunWszystkie,
  wszystkieUsuniete,
  zmianyZEdycji,
  type EdycjaWyniku,
} from '@/lib/edycja-wyniku';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  SIATKA EDYCJI WYNIKU — etap 2 „jak SSMS" (28.09.2026).
 *
 *  ⚠️⚠️ ZMIANA IDZIE NA CAŁĄ WYSPĘ, NIGDY DO WYBRANEJ BAZY. Nawet gdy oglądasz bazę maszyny,
 *  edycja staje się zmianą config-down dla wyspy: najpierw master, potem maszyny. Przycisku
 *  „zapisz tylko tutaj" nie ma i mieć nie będzie — to on robiłby rozjazdy (ustalone 28.09.2026).
 *
 *  ⚠️ WYSYŁKA PRZEZ WSPÓLNY PODGLĄD (`PodgladZmian`): werdykt bramki dla każdej zmiany, ostrzeżenia
 *  do potwierdzenia i uzasadnienie — dokładnie jak w zakładce „Zmiany".
 *
 *  ⚠️ ZMIANA HURTOWA OD 29.09.2026 (etap 3): powyżej `MAX_ZMIAN_W_OPERACJI` zmian wysyłka idzie
 *  przez `PodgladHurtu` — kilka operacji, podział pokazany i potwierdzany przed wysyłką. Narzędzia
 *  „ustaw we wszystkich" i „usuń wszystkie" działają tylko na PEŁNYM wyniku (bez obcięcia).
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Opis zmiany jednym wierszem — do listy „do wysłania". */
function opisZmiany(z: DraftChange): string {
  const klucz = Object.entries(z.pk)
    .map(([k, v]) => `${k}=${v}`)
    .join(', ');
  const dane = Object.entries(z.payload ?? {})
    .filter(([k]) => !(k in z.pk))
    .map(([k, v]) => `${k} → ${v === null ? 'NULL' : String(v)}`)
    .join(', ');
  return `${z.tableName} ${z.action} ${klucz}${dane === '' ? '' : ` · ${dane}`}`;
}

/**
 * Droga rozsyłki — w Smart Syncu master przed maszynami, w trybie zwykłym jedna baza wyspy.
 * Ta sama kolejność przed wysyłką („Pójdzie:") i po niej (stan na węzłach, `StanWysylki`).
 */
export function drogaRozsylki(wezly: readonly Pick<WezelBazy, 'master' | 'nodeId'>[]): { krok: number; nodeId: string }[] {
  const master = wezly.filter((w) => w.master);
  const reszta = wezly.filter((w) => !w.master);
  if (master.length === 0) return reszta.map((w) => ({ krok: 1, nodeId: w.nodeId }));
  return [...master.map((w) => ({ krok: 1, nodeId: w.nodeId })), ...reszta.map((w) => ({ krok: 2, nodeId: w.nodeId }))];
}

export function SiatkaEdycji({
  groupId,
  wynik,
  tabela,
  wezly,
  kolejnosc,
  onWyslano,
  onZakoncz,
}: {
  groupId: string;
  wynik: WynikZapytania;
  tabela: AuthoringTable;
  wezly: readonly WezelBazy[];
  /**
   * Kolejność wierszy z siatki odczytu (sortowanie po nagłówku). ⚠️ Edycja trzyma się INDEKSU
   * wiersza z wyniku, a nie pozycji na ekranie — posortowana kopia podpięłaby zmianę pod inny wiersz.
   */
  kolejnosc?: readonly number[];
  /**
   * Numery operacji z kolejki — po nich ekran pokazuje stan na węzłach. Przy hurcie może ich być
   * kilka, a `blad` mówi, że któraś z kolejnych części nie weszła (te z `opIds` weszły i pójdą).
   */
  onWyslano: (opIds: string[], blad: string | null) => void;
  onZakoncz: () => void;
}) {
  const { t } = useI18n();
  const [edycja, setEdycja] = useState<EdycjaWyniku>(pustaEdycja);
  const [podglad, setPodglad] = useState(false);

  const { zmiany, bledy } = useMemo(() => zmianyZEdycji(wynik, tabela, edycja), [wynik, tabela, edycja]);
  const droga = drogaRozsylki(wezly);
  /** Powyżej jednej operacji — podgląd hurtu z podziałem do potwierdzenia. */
  const hurtem = zmiany.length > MAX_ZMIAN_W_OPERACJI;
  const zaDuzo = zmiany.length > MAX_ZMIAN_HURTEM;
  /** Kolumny do „ustaw we wszystkich" — te same, które da się zmienić w komórce. */
  const kolumnyHurtu = wynik.kolumny.flatMap((_, c) => (kolumnaEdytowalna(wynik, tabela, c) !== null ? [c] : []));
  const [hurtKol, setHurtKol] = useState<number | null>(null);
  const [hurtWartosc, setHurtWartosc] = useState('');
  const [bladHurtu, setBladHurtu] = useState<string | null>(null);
  /**
   * ⚠️ OBCIĘTY WYNIK BLOKUJE NARZĘDZIA „WSZYSTKIE": filtr pasuje do większej liczby wierszy, niż
   * przyszło, więc „wszystkie" byłoby nieprawdą — zmiana objęłaby pierwszy tysiąc, a resztę po cichu nie.
   */
  const obciety = wynik.obcieto === true;
  const usunieteWszystkie = wszystkieUsuniete(wynik, edycja);
  /** Wartość sprawdzona RAZ, przed wpisaniem do każdego wiersza — inaczej tysiąc takich samych błędów. */
  const ustawHurtem = (): void => {
    const kol = hurtKol === null ? null : kolumnaEdytowalna(wynik, tabela, hurtKol);
    if (hurtKol === null || kol === null) return;
    const v = validateCell(kol.type, hurtWartosc);
    if (v.error !== null) {
      setBladHurtu(`${kol.name}: ${v.error}`);
      return;
    }
    setBladHurtu(null);
    setEdycja((e) => ustawWszedzie(wynik, e, hurtKol, hurtWartosc));
  };
  const moznaDodac = tabela.actions.includes('I');
  /** Usuwanie tylko w tabelach-liściach — katalog daje `D` wyłącznie tam (etap 3, 29.09.2026). */
  const moznaUsuwac = tabela.actions.includes('D');
  const przelaczUsuniecie = (r: number): void =>
    setEdycja((e) => ({ ...e, usuniete: e.usuniete.includes(r) ? e.usuniete.filter((x) => x !== r) : [...e.usuniete, r] }));
  const kolumnyNowego = [...tabela.key, ...tabela.columns.filter((k) => k.editable && k.type !== null)];

  const ustawKomorke = (r: number, c: number, raw: string): void =>
    setEdycja((e) => ({ ...e, zmienione: { ...e.zmienione, [`${r}:${c}`]: raw } }));
  const ustawNowy = (id: string, kol: string, raw: string): void =>
    setEdycja((e) => ({ ...e, nowe: e.nowe.map((n) => (n.id === id ? { ...n, wartosci: { ...n.wartosci, [kol]: raw } } : n)) }));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="flex items-center gap-1 font-medium">
          <Pencil className="size-3.5" />
          {t('baza.edycjaTabeli').replace('{tabela}', tabela.name)}
        </span>
        {moznaDodac && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEdycja((e) => ({ ...e, nowe: [...e.nowe, { id: crypto.randomUUID(), wartosci: {} }] }))}
          >
            <Plus className="size-3.5" />
            {t('baza.dodajWiersz')}
          </Button>
        )}
        <Button size="sm" variant="ghost" className="ml-auto" onClick={onZakoncz}>
          <X className="size-3.5" />
          {t('baza.zakonczEdycje')}
        </Button>
      </div>

      {wynik.wiersze.length > 0 && (kolumnyHurtu.length > 0 || moznaUsuwac) && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {kolumnyHurtu.length > 0 && (
            <>
              <select
                aria-label={t('baza.hurtKolumna')}
                value={hurtKol ?? ''}
                disabled={obciety}
                onChange={(e) => setHurtKol(e.target.value === '' ? null : Number(e.target.value))}
                className="rounded border border-border bg-background px-1 py-0.5 font-mono"
              >
                <option value="">{t('baza.hurtKolumna')}</option>
                {kolumnyHurtu.map((c) => (
                  <option key={c} value={c}>
                    {wynik.kolumny[c]!.nazwa}
                  </option>
                ))}
              </select>
              <input
                aria-label={t('baza.hurtWartosc')}
                placeholder={t('baza.hurtWartosc')}
                value={hurtWartosc}
                disabled={obciety}
                onChange={(e) => setHurtWartosc(e.target.value)}
                className="w-40 rounded border border-border bg-background px-1 py-0.5 font-mono"
              />
              <Button size="sm" variant="outline" disabled={obciety || hurtKol === null} onClick={ustawHurtem}>
                {t('baza.hurtUstaw')}
              </Button>
            </>
          )}
          {moznaUsuwac && (
            <Button
              size="sm"
              variant="outline"
              disabled={obciety}
              onClick={() => setEdycja((e) => (usunieteWszystkie ? { ...e, usuniete: [] } : usunWszystkie(wynik, e)))}
            >
              {usunieteWszystkie ? <Undo2 className="size-3.5" /> : <Trash2 className="size-3.5 text-[var(--destructive)]" />}
              {t(usunieteWszystkie ? 'baza.hurtPrzywrocWszystkie' : 'baza.hurtUsunWszystkie')}
            </Button>
          )}
          {obciety && <span className="text-[var(--warning)]">{t('baza.hurtObciety')}</span>}
          {bladHurtu !== null && <span className="text-[var(--destructive)]">{bladHurtu}</span>}
        </div>
      )}

      <div className="max-h-[36rem] overflow-auto rounded-md border border-border">
        <table className="w-max min-w-full border-collapse text-xs">
          <thead className="sticky top-0 z-10 bg-card">
            <tr>
              <th className="border-b border-border px-2 py-1 text-right font-normal text-muted-foreground">#</th>
              {wynik.kolumny.map((k, c) => (
                <th key={`${k.nazwa}-${c}`} title={k.typ} className="border-b border-border px-2 py-1 text-left font-medium">
                  <span className="flex items-center gap-1">
                    {k.nazwa}
                    {kolumnaEdytowalna(wynik, tabela, c) !== null && <Pencil className="size-3 text-muted-foreground" />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {edycja.nowe.map((n, i) => (
              <tr key={n.id} className="border-b border-border/50 bg-[var(--success)]/10">
                <td className="px-2 py-0.5 text-right">
                  <button
                    type="button"
                    aria-label={t('baza.usunNowy')}
                    title={t('baza.usunNowy')}
                    onClick={() => setEdycja((e) => ({ ...e, nowe: e.nowe.filter((x) => x.id !== n.id) }))}
                  >
                    <Trash2 className="size-3 text-muted-foreground" />
                  </button>
                </td>
                {wynik.kolumny.map((k, c) => {
                  const kol = kolumnyNowego.find((x) => x.name.toLowerCase() === k.nazwa.toLowerCase());
                  return (
                    <td key={c} className="px-1 py-0.5">
                      {kol === undefined ? (
                        <span className="px-1 text-muted-foreground">—</span>
                      ) : (
                        <input
                          aria-label={`${t('baza.nowyWiersz')} ${i + 1}, ${kol.name}`}
                          value={n.wartosci[kol.name] ?? ''}
                          onChange={(e) => ustawNowy(n.id, kol.name, e.target.value)}
                          className="w-full min-w-[6rem] rounded border border-border bg-background px-1 py-0.5 font-mono"
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {(kolejnosc ?? wynik.wiersze.map((_, i) => i)).map((r, poz) => {
              const usuniety = edycja.usuniete.includes(r);
              return (
                <tr key={r} className={cn('border-b border-border/50', usuniety && 'bg-[var(--destructive)]/10 line-through')}>
                  <td className="whitespace-nowrap px-2 py-0.5 text-right tabular-nums text-muted-foreground">
                    {moznaUsuwac && (
                      <button
                        type="button"
                        className="mr-1 align-middle"
                        aria-label={`${t(usuniety ? 'baza.przywrocWiersz' : 'baza.usunWiersz')} ${poz + 1}`}
                        title={t(usuniety ? 'baza.przywrocWiersz' : 'baza.usunWiersz')}
                        onClick={() => przelaczUsuniecie(r)}
                      >
                        {usuniety ? <Undo2 className="size-3" /> : <Trash2 className="size-3 text-[var(--destructive)]" />}
                      </button>
                    )}
                    {poz + 1}
                  </td>
                  {wynik.wiersze[r]!.map((v, c) => {
                    const kol = usuniety ? null : kolumnaEdytowalna(wynik, tabela, c);
                    const adres = `${r}:${c}`;
                    const wpisane = edycja.zmienione[adres];
                    const zmieniona = wpisane !== undefined && wpisane !== (v ?? '');
                    if (kol === null) {
                      return (
                        <td key={c} className="max-w-[28rem] truncate whitespace-nowrap px-2 py-0.5 font-mono text-muted-foreground">
                          {v === null ? <span className="italic">NULL</span> : v}
                        </td>
                      );
                    }
                    return (
                      <td key={c} className="px-1 py-0.5">
                        <input
                          aria-label={`${t('baza.wiersz')} ${r + 1}, ${kol.name}`}
                          value={wpisane ?? v ?? ''}
                          placeholder={v === null ? 'NULL' : undefined}
                          onChange={(e) => ustawKomorke(r, c, e.target.value)}
                          className={cn(
                            'w-full min-w-[6rem] rounded border bg-background px-1 py-0.5 font-mono',
                            zmieniona ? 'border-[var(--warning)] bg-[var(--warning)]/15' : 'border-transparent hover:border-border',
                          )}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/*
        ⚠️ „CO POJEDZIE W DÓŁ" PRZED WYSYŁKĄ — lista zmian i droga rozsyłki. Zamówione wprost:
        „nie wiadomo co tam ustawiamy … i co pójdzie w dół".
      */}
      <div className="space-y-2 rounded-md border border-border p-2 text-xs">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{t('baza.doWyslania').replace('{n}', String(zmiany.length))}</span>
          <span className="text-muted-foreground">{t('baza.pojdzie')}</span>
          {droga.map((d, i) => (
            <span key={d.nodeId} className="flex items-center gap-1">
              {i > 0 && droga[i - 1]!.krok !== d.krok && <ArrowRight className="size-3 text-muted-foreground" />}
              <span className="rounded border border-border px-1.5 py-0.5 font-mono">
                {d.krok} · {d.nodeId}
              </span>
            </span>
          ))}
        </div>
        {zmiany.length > 0 && (
          <ul className="max-h-40 space-y-0.5 overflow-y-auto font-mono text-muted-foreground">
            {zmiany.map((z, i) => (
              <li key={i}>{opisZmiany(z)}</li>
            ))}
          </ul>
        )}
        {bledy.map((b) => (
          <p key={b} className="text-[var(--destructive)]">
            {b}
          </p>
        ))}
        {hurtem && !zaDuzo && (
          <p className="text-muted-foreground">
            {t('baza.hurtCzesci').replace('{k}', String(liczbaOperacji(zmiany, MAX_ZMIAN_W_OPERACJI)))}
          </p>
        )}
        {zaDuzo && <p className="text-[var(--destructive)]">{t('baza.zaDuzoZmian').replace('{n}', String(MAX_ZMIAN_HURTEM))}</p>}
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" disabled={zmiany.length === 0} onClick={() => setEdycja(pustaEdycja())}>
            {t('baza.odrzucZmiany')}
          </Button>
          <Button size="sm" disabled={zmiany.length === 0 || bledy.length > 0 || zaDuzo} onClick={() => setPodglad(true)}>
            <Send className="size-3.5" />
            {t('baza.podgladWysylka')}
          </Button>
        </div>
      </div>

      {podglad &&
        (hurtem ? (
          <PodgladHurtu
            groupId={groupId}
            changes={zmiany}
            onClose={() => setPodglad(false)}
            onQueued={(opIds, blad) => {
              setEdycja(pustaEdycja());
              onWyslano(opIds, blad);
            }}
          />
        ) : (
          <PodgladZmian
            groupId={groupId}
            changes={zmiany}
            onClose={() => setPodglad(false)}
            onQueued={(opId) => {
              setEdycja(pustaEdycja());
              onWyslano([opId], null);
            }}
          />
        ))}
    </div>
  );
}

/** Dlaczego tego wyniku nie da się edytować — `null`, gdy się da. Klucz musi być w wyniku. */
export function powodBrakuEdycji(wynik: WynikZapytania, tabela: AuthoringTable | null, nazwa: string | null): string | null {
  if (nazwa === null) return 'Edycja tylko dla wyniku z jednej tabeli (bez JOIN)';
  if (tabela === null) return `Tabelę ${nazwa} można tylko czytać — edytor jej nie otwiera`;
  const brak = tabela.key.filter((k) => indeksKolumny(wynik, k.name) < 0).map((k) => k.name);
  if (brak.length > 0) return `W wyniku brakuje kolumn klucza: ${brak.join(', ')}`;
  return null;
}
